import React, { useState, useEffect, useContext } from 'react';
import { Container, Grid, Card, CardContent, CardMedia, Box } from '@mui/material';
import CameraIcon from '@mui/icons-material/Camera';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CakeIcon from '@mui/icons-material/Cake';
import PregnantWoman from '@mui/icons-material/PregnantWoman';
import Person from '@mui/icons-material/Person';
import NaturePeople from '@mui/icons-material/NaturePeople';
import '../styles/css/Services.css';
// Import motion components
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { LanguageContext } from '../App';

// Import images


const Services = () => {
  const [mounted, setMounted] = useState(false);
  
  // Animation controls
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const { language, translations } = useContext(LanguageContext);

  // Set mounted state after initial render to ensure we're in browser environment
  useEffect(() => {
    setMounted(true);
  }, []);
  
  // Start animation when section comes into view
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const handleScrollToContact = () => {
    if (typeof document !== 'undefined') {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 12,
        stiffness: 70
      }
    }
  };

  // Update the handleCardClick function to open links in a new tab
  const handleCardClick = (service) => {
    // Store the selected service in sessionStorage for potential use in the contact form
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('selectedService', service.title);
    }
    
    // Open the service link in a new tab
    if (service.link) {
      window.open(service.link, '_blank', 'noopener,noreferrer');
    }
  };

  // Use translated services
  const services = translations[language].services;

  return (
    <Box className="services-section">
      <Container>
        <motion.div
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={containerVariants}
        >
          <motion.div variants={itemVariants}>
            <h2 className="section-title">
              {translations[language].servicesTitle}
            </h2>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <p className="section-subtitle">
              {translations[language].servicesSubtitle}
            </p>
          </motion.div>
          
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div variants={itemVariants}>
                  <Card 
                    className="service-card"
                    onClick={() => handleCardClick(service)}
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                      }
                    }}
                    component={motion.div}
                    whileHover={{ 
                      y: -10,
                      boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.2)"
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={service.image}
                      alt={service.title}
                      className="service-image"
                    />
                    <CardContent className="service-content">
                      <motion.div 
                        className="service-icon"
                        whileHover={{ rotate: [0, -10, 10, -10, 0], transition: { duration: 0.5 } }}
                      >
                        {service.icon}
                      </motion.div>
                      <div className="service-title">
                        {service.title}
                      </div>
                      <p className="service-description">
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
}

export default Services;
